import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { Appointment } from '@/store/appointment/appointment.model';
import { AppointmentSchema } from '@/store/appointment/appointment.validator';
import { AppointmentAction, AppointmentGetter } from '@/store/appointment/appointment.module';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'AppointmentFormComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  props: {
    appointmentId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.appointmentId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูลหนังสือคำสั่ง' : 'เพิ่มข้อมูลหนังสือคำสั่ง');
    const appointment = computed<Appointment | undefined>(() => store.getters[AppointmentGetter.BY_ID](props.appointmentId));
    const appointmentForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      name?: string | null;
      startDate?: Date | null;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      startDate: null,
    });
    const validator = computed(() => AppointmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      startDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'startDate')),
    });
    const isValid = computed(() => !errors.name && !errors.startDate);
    const isLoading = computed<boolean>(() => store.getters[AppointmentGetter.STATUS] === 'init' || store.getters[AppointmentGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.appointmentId;
          formData.name = appointment.value?.name;
          formData.startDate = appointment.value?.startDate;
        }
        resolve();
      })
      .then(() => appointmentForm.value?.show());
    };

    // hide modal
    const hide = () => {
      appointmentForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.startDate = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(AppointmentAction.UPDATE, formData)
            .then(() => store.dispatch(AppointmentAction.FETCH)
              .then(() => hide()));
        } else {
          store.dispatch(AppointmentAction.CREATE, formData)
            .then(() => store.dispatch(AppointmentAction.FETCH)
              .then(() => hide()));
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(AppointmentAction.DELETE, formData)
              .then(() => store.dispatch(AppointmentAction.FETCH)
                .then(() => hide()));
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      appointmentForm,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
    };
  }
});
